import { useEffect, useState, useMemo } from 'react';
import { ActionIcon, Badge, Button, CopyButton, Divider, Group, MantineColor, Title, Tooltip, useMantineTheme } from '@mantine/core';
import { openConfirmModal, useModals } from '@mantine/modals';
import { Check, Copy, Trash, ExternalLink } from 'tabler-icons-react';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { FormatService } from '@root/Services/FormatService';
import { useAuthZValues } from '@root/Services/AuthorizationService';
import { useDi, useDiComponent } from '@root/Services/DI';
import { FillerSwitch } from '@root/Design/Filler';
import { PagePanel, PaneledPage, PanelBody, PanelHeader } from '@root/Design/Layout';
import { DataColumnConfig, DataGridState } from '@root/Components/DataGrid/Models';
import { DataGrid } from '@root/Components/DataGrid';
import { GridFullCell } from '@root/Components/DataGrid/Design';
import { useAccessLookup } from '@root/Components/Shell/AppFeatureAccess';
import { useCompanyUserList } from '@root/Components/Users/UserHooks';
import { getExternalApiKeyGetCompanyApiKeys, putExternalApiKeyRevokeApiKey } from '@apis/Customers';
import { ApiAccessKeyResult, ExternalApiKey, UserListItem } from '@apis/Customers/model';
import { SettingsPage } from './SettingsPage';
import { ApiAccessKeyModal } from './ApiAccessKeyModal';
import { ApiAccessKeyCreatedModal } from './ApiAccessKeyCreatedModal';
import { ConfigService } from '@root/Services/ConfigService';
import { CustomColors, theme } from '@root/Design/Themes';

export function APIAccess() {
    const modals = useModals();
    const DiContainer = useDiComponent();
    const theme = useMantineTheme();
    const configSvc = useDi(ConfigService);
    const [loading, setLoading] = useState(true);
    const accessLookup = useAccessLookup();
    type ExternalApiKeyWithStatus = ExternalApiKey & { status: 'Active' | 'Revoked' | 'Expired' };
    const [apiKeys, setAPIKeys] = useState<ExternalApiKeyWithStatus[]>([]);
    const [users, setUsers] = useState<UserListItem[]>();
    const formatSvc = useDi(FormatService);
    const userPermissions = useAuthZValues({
        canManage: { APIAccess: 'Manage' },
        canAdmin: { TenantAPIAccess: 'Manage' },
    });
    const { loading: usersLoading, userList } = useCompanyUserList();

    useEffect(() => {
        loadAPIKeys();
    }, [accessLookup]);

    const loadAPIKeys = async () => {
        if (accessLookup) {
            setLoading(true);
            const hasTenantMgmtAccess = accessLookup.checkAccessByName('API Access', 'Execute');
            await getExternalApiKeyGetCompanyApiKeys()
                .then((response) => {
                    const now = new Date();
                    setAPIKeys(
                        response.map((k) => ({
                            ...k,
                            status: !k.IsActive ? 'Revoked' : getApiKeyExpiration(k) ? 'Expired' : 'Active',
                        }))
                    );
                })
                .finally(() => setLoading(false));
        }
    };

    const openAddAPIKeyModal = () => {
        const onClose = (didCreate: boolean, result?: ApiAccessKeyResult) => {
            modals.closeModal(id);
            if (didCreate && result) {
                loadAPIKeys();
                openCreatedApiKeyModal(result);
            }
        };
        const id = modals.openModal({
            title: 'Create API Key',
            children: (
                <DiContainer>
                    <ApiAccessKeyModal onClose={onClose}></ApiAccessKeyModal>
                </DiContainer>
            ),
        });
    };

    const openCreatedApiKeyModal = (key: ApiAccessKeyResult) => {
        if (key.PlainKey && key.PlainSecret) {
            const onClose = () => modals.closeModal(id);
            const id = modals.openModal({
                title: 'Your New API Key and Secret',
                size: '50%',
                closeOnClickOutside: false,
                children: (
                    <DiContainer>
                        <ApiAccessKeyCreatedModal rawKey={key.PlainKey} rawSecret={key.PlainSecret} onClose={onClose}></ApiAccessKeyCreatedModal>
                    </DiContainer>
                ),
            });
        }
    };

    const getApiKeyExpiration = (key: ExternalApiKey): boolean => {
        const now = new Date();
        let expirationDate: Date | null = null;

        if (key.Expiration != null) {
            expirationDate = new Date(key.Expiration as string | number | Date);
        }
        return expirationDate !== null && expirationDate < now;
    };

    const { gridState, apiKeyColumns } = useMemo(() => {
        const apiKeyUsers = [...new Set(apiKeys?.map((key) => userList?.getById(key.UserId ?? 0)?.fullName).filter((n) => n))].sort();
        const apiKeyColumns = [
            {
                id: 'Name',
                header: 'Name',
                accessor: 'Name',
                defaultWidth: 200,
                type: 'string',
            },
            {
                id: 'ApiKey',
                header: 'Api Key',
                accessor: (u) => u.ApiKey,
                defaultWidth: 500,
                cellRenderer: (key) => {
                    return (
                        <>
                            <Group>
                                <>{key.ApiKey}</>
                                {key.status === 'Active' ? (
                                    <CopyButton value={key.ApiKey!}>
                                        {({ copied, copy }) => (
                                            <Tooltip withinPortal withArrow position="bottom" label={copied ? 'Copied' : 'Copy API Key'}>
                                                <Button
                                                    variant="subtle"
                                                    onClick={copy}
                                                    style={{ height: '18px', paddingRight: '4px', paddingLeft: '4px' }}
                                                >
                                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                ) : (
                                    <></>
                                )}
                            </Group>
                        </>
                    );
                },
            },
            {
                id: 'User',
                header: 'User',
                defaultHidden: !userPermissions.canAdmin,
                hideInColumnSelector: !userPermissions.canAdmin,
                defaultWidth: 200,
                sortField: 'User',
                type: 'string',
                accessor: (key) => userList?.getById(key.UserId ?? 0)?.fullName ?? '',
                filter: {
                    filterType: 'string',
                    name: 'User',
                    filterField: 'User',
                    options: {
                        getValueProvider: () => apiKeyUsers.map((n) => ({ label: n, value: n })),
                    },
                },
            },
            {
                id: 'Expiration',
                header: 'Expiration',
                defaultWidth: 200,
                sortField: 'Expiration',
                type: 'date',
                accessor: (u) => u.Expiration,
                cellRenderer: (key) => formatSvc.toLocal(key.Expiration),
                filter: {
                    filterType: 'date',
                    name: 'Expiration',
                    filterField: 'Expiration',
                },
            },
            {
                id: 'Status',
                header: 'Status',
                accessor: 'status',
                defaultWidth: 100,
                cellRenderer: (key) => {
                    const expiration = getApiKeyExpiration(key);
                    const now = new Date();
                    const color: MantineColor = key.status === 'Active' ? 'success' : 'error';
                    return <Badge color={color}>{key.status}</Badge>;
                },
                type: 'string',
                filter: {
                    name: 'Status',
                    filterType: 'string',
                    filterField: 'status',
                    options: {
                        getValueProvider: () => ['Active', 'Expired', 'Revoked'].map((label) => ({ label, value: label })),
                    },
                },
            },
            {
                accessor: () => '',
                id: 'Revoke',
                header: '',
                defaultWidth: 100,
                type: 'string',
                cellRenderer: (key) => {
                    if (key.status === 'Active' && (userPermissions.canAdmin || userPermissions.canManage)) {
                        return (
                            <GridFullCell className="delete-icon" style={{ justifyContent: 'center', display: 'flex' }}>
                                <Tooltip withinPortal withArrow position="bottom" label="Revoke API Key">
                                    <ActionIcon onClick={() => revokeKey(key)}>
                                        <Trash size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </GridFullCell>
                        );
                    } else {
                        return <></>;
                    }
                },
                exportOptions: {
                    hidden: true,
                },
            },
        ] as DataColumnConfig<ExternalApiKeyWithStatus>[];
        const gridState: DataGridState = {
            columns: apiKeyColumns.filter((c) => !c.defaultHidden).map((c) => ({ id: c.id, width: c.defaultWidth })),
            filters: [],
            sort: [],
        };
        return { gridState, apiKeyColumns };
    }, [apiKeys, userList]);

    const revokeKey = async (key: ExternalApiKey) => {
        const user = users?.find((user) => user.Id === key.UserId);
        const name = user ? `${user.FirstName} ${user.LastName}` : 'System';
        openConfirmModal({
            title: 'Revoke API Key?',
            children: (
                <>
                    <p>
                        Are you sure you want to revoke the API key for <strong>{name}</strong>?
                    </p>
                </>
            ),
            centered: true,
            labels: { confirm: 'Yes', cancel: 'Cancel' },
            onConfirm: async () => {
                try {
                    setLoading(true);
                    await putExternalApiKeyRevokeApiKey({ externalApiKeyId: key.Id }).then(() => {
                        loadAPIKeys();
                    });
                } catch (error) {
                    // notify
                    console.error('Failed to revoke API key:', error);
                } finally {
                    setLoading(false);
                }
            },
            confirmProps: { color: 'error' },
        });
    };

    const openApiDocs = () => {
        var url = configSvc.config.apiDocSiteUrl;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <SettingsPage>
            <PaneledPage>
                <PagePanel size="fill">
                    <PanelHeader>
                        <Title order={3}>API Access</Title>
                        <Group spacing="sm">
                            {userPermissions.canManage ? <Button onClick={openAddAPIKeyModal}>New API Key</Button> : <></>}
                            <Button variant="outline" rightIcon={<ExternalLink />} onClick={openApiDocs}>
                                View API Docs
                            </Button>
                        </Group>
                    </PanelHeader>
                    <Divider />
                    <PanelBody mode="compact">
                        <FillerSwitch
                            loading={loading || !accessLookup || usersLoading}
                            noData={apiKeys.length === 0}
                            noDataMessage="No API Keys Found"
                        >
                            {() => (
                                <DataGrid
                                    columns={apiKeyColumns}
                                    dataSource={apiKeys}
                                    state={gridState}
                                    statePersistence={{ key: 'APIKeys' }}
                                    allowSavedViews
                                    hideGlobalSearch={true}
                                    hideColumnSelector={true}
                                ></DataGrid>
                            )}
                        </FillerSwitch>
                    </PanelBody>
                </PagePanel>
            </PaneledPage>
        </SettingsPage>
    );
}
endpoint('api-access', APIAccess, 'ApiAccess');
